import * as React from "react";
import { KEYBOARD_KEYS } from "constants/keyboard";
import { ICellEditor, ICellEditorParams } from "ag-grid-community";
import { IChoiceValue } from "../../components/product-collection/product-collection-views/product-collection-grid-view/grid-editors/single-choice-cell-editor";
import { OrderedSet } from "immutable";
import { formatSizePackValues } from "helpers/product-collection";
import { MultipleChoiceCellEditor } from "../../components/product-collection/product-collection-views/product-collection-grid-view/grid-editors/multiple-choice-cell-editor";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import { IStore } from "reducers";
import { amphibiousGetIn, amphibiousSetIn } from "helpers/objUtils";

interface IProps extends ICellEditorParams {
    getAvailableValues: (stateChunk: any, itemData: any) => IChoiceValue[];
    delimiter?: string;
    fetchSuggestions?: (...args: any[]) => { type: string; [key: string]: any } | undefined;
    statePaths?: string[][];
    parseCurrentValue: (value: string, delimiter?: string) => any[];
}

interface IContainerProps {
    setValue: (value: string) => void;
}

interface IReduxProps {
    onFetchSuggestions?: (stateChunk: any) => void;
    stateChunk?: any;
}

interface IState {
    availableValues: IChoiceValue[];
    checkedValues: OrderedSet<string>;
}

class MultipleChoiceCellEditorComponent
    extends React.PureComponent<IProps & IReduxProps & IContainerProps, IState>
    implements ICellEditor
{
    constructor(props) {
        super(props);
        const availableValues = props.getAvailableValues(props.stateChunk, props.data);
        const availableIds = availableValues.map(({ id }) => id);
        let checkedValues = OrderedSet<string>(
            props.value
                ? props.parseCurrentValue(props.value, props.delimiter).filter((id) => availableIds.includes(id))
                : []
        );
        const { eventKey } = this.props;

        if (eventKey === KEYBOARD_KEYS.BACKSPACE || eventKey === KEYBOARD_KEYS.DELETE) {
            checkedValues = OrderedSet();
        }

        this.state = { checkedValues, availableValues };
    }

    getValue = () => {
        return formatSizePackValues(this.state.checkedValues);
    };

    handleCheckboxChange = (value) => {
        const { checkedValues } = this.state;
        this.setState({
            checkedValues: checkedValues.has(value) ? checkedValues.remove(value) : checkedValues.add(value),
        });
    };

    handleSelectAll = () => {
        const { checkedValues, availableValues } = this.state;
        this.setState({
            checkedValues: OrderedSet(
                checkedValues.size === availableValues.length ? [] : availableValues.map(({ id }) => id)
            ),
        });
    };

    render() {
        const { checkedValues, availableValues } = this.state;
        return (
            <MultipleChoiceCellEditor
                availableValues={availableValues}
                checkedValues={checkedValues}
                onSelectAll={this.handleSelectAll}
                onSelect={this.handleCheckboxChange}
            />
        );
    }

    componentDidMount() {
        const { onFetchSuggestions, stateChunk } = this.props;
        onFetchSuggestions?.(stateChunk);
    }

    componentDidUpdate(prevProps) {
        const { data, getAvailableValues, stateChunk } = this.props;
        if (prevProps.stateChunk !== stateChunk) {
            const newValues = getAvailableValues(stateChunk, data);
            this.setState({ availableValues: newValues });
        }
    }
}

const mapDispatchToProps = (dispatch, ownProps: IProps) => {
    const { fetchSuggestions, data } = ownProps;
    return {
        ...(fetchSuggestions
            ? {
                  onFetchSuggestions(stateChunk) {
                      const suggestionsAction = fetchSuggestions(stateChunk, data);
                      suggestionsAction && dispatch(suggestionsAction);
                  },
              }
            : {}),
    };
};

const mapStateToProps = (state: IStore, ownProps: IProps) => {
    const { statePaths } = ownProps;
    if (statePaths) {
        return {
            stateChunk: statePaths.reduce((acc, path) => {
                amphibiousSetIn(acc, path, amphibiousGetIn(state, path));
                return acc;
            }, {}),
        };
    }
    return {};
};

const MultipleChoiceCellEditorContainer = withRouter(
    connect(mapStateToProps, mapDispatchToProps, null, { withRef: true })(MultipleChoiceCellEditorComponent),
    {
        withRef: true,
    }
);

export class MultipleChoiceCellEditorWrapper extends React.PureComponent<IProps> {
    private containerRef;
    constructor(props) {
        super(props);
    }

    attachRef = (element) => {
        if (element) {
            const wrappedContainer = element.getWrappedInstance();
            this.containerRef = wrappedContainer?.wrappedInstance;
        }
    };

    getValue() {
        return this.containerRef?.getValue() ?? this.props.value;
    }

    isPopup(): boolean {
        return true;
    }

    render() {
        return <MultipleChoiceCellEditorContainer {...this.props} ref={this.attachRef} />;
    }
}
